import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiMinus, FiHelpCircle, FiPhoneCall } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useLightbox } from '../../context/LightboxContext';
import { BUSINESS_WHATSAPP_CHAT_URL } from '../../config/businessInfo';
import './HomeFaqSection.css';

const FAQ_SHOWROOM_IMG = '/assets/media/images/team/employee_interacting.jpg';

const HOME_FAQS = [
    {
        id: 'faq-1',
        question: 'How does buying appliances on installment work?',
        answer: 'Choose any appliance, pick your down payment (25%, 50%, or 75%) and a flexible tenure of 4, 6, 9, or 12 months (or our special 0% markup 4-month plan). Submit your order and our showroom specialist calls you to verify details. Verification is completed within 24 working hours.',
    },
    {
        id: 'faq-2',
        question: 'What documents and verification are required for installments?',
        answer: 'We require a valid CNIC photocopy, recent electricity/gas utility bill copy, and contact details for 2 personal guarantors (relatives, friends, or neighbors in Lahore). You can visit our showroom or request our Verification Officer to visit your home (Rs. 1,000 to Rs. 2,000 visiting and petrol charges apply, non-refundable). Note: Bank cheques are not mandatory, but may be requested if needed.',
    },
    {
        id: 'faq-3',
        question: 'Do I get a discount if I pay in cash upfront?',
        answer: 'Yes! Every cash purchase receives an automatic instant discount off the official manufacturer MRP, clearly displayed across all our product cards and catalog.',
    },
    {
        id: 'faq-4',
        question: 'Can I pay my installments early or in advance?',
        answer: 'Yes, you can pay off your remaining balance early at any time! However, no additional discount is offered on early payoff to strictly adhere to Islamic financing rules. In Islam, once a contract and agreed total sale price are finalized, the terms remain fixed. Altering the price for early payoff would introduce interest (Riba), so the agreed contract terms stay fixed and Shariah-compliant.',
    },
    {
        id: 'faq-5',
        question: 'How fast is delivery to my doorstep in Lahore?',
        answer: 'We provide fast doorstep showroom delivery exclusively within Lahore for in-stock appliances confirmed before 5:00 PM. Our dedicated showroom crew ensures safe transportation and careful doorstep handling.',
    },
    {
        id: 'faq-6',
        question: 'Are all products 100% genuine with official brand warranties?',
        answer: 'Yes, 100%! All appliances come brand new in factory-sealed boxes with official company warranty cards from our 18 authorized partner brands (Dawlance, PEL, Orient, Multynet, Konvo, TCL, Gree, Eco Star, United, Care Appliances, Super Asia, Waves, Enviro, Samsung, Boss Appliances, Sabro Appliances, izone, and F&D Speakers). All warranty claims, parts, and compressor coverage are directly serviced and honored by the official brand manufacturing companies and their authorized service centers across Lahore and Pakistan.',
    },
];

const HomeFaqSection = () => {
    // All FAQs start closed by default (openId = null)
    const [openId, setOpenId] = useState(null);
    const { openLightbox } = useLightbox();

    const toggleFaq = (id) => {
        setOpenId((prev) => (prev === id ? null : id));
    };

    return (
        <section className="home-faq-section" aria-label="Frequently asked questions">
            <div className="container">
                <div className="home-faq-header">
                    <span className="section-eyebrow">VERIFIED ANSWERS</span>
                    <h2 className="section-title">Frequently Asked Questions</h2>
                    <p className="home-faq-subtitle">
                        Clear, transparent information about financing, warranties, delivery, and cash discounts
                    </p>
                </div>

                <div className="home-faq-layout">
                    {/* Left: Authentic Interactive Accordion */}
                    <div className="home-faq-list">
                        {HOME_FAQS.map((faq) => {
                            const isOpen = openId === faq.id;
                            return (
                                <div
                                    key={faq.id}
                                    className={`home-faq-card ${isOpen ? 'is-open' : ''}`}
                                >
                                    <button
                                        type="button"
                                        className="home-faq-trigger"
                                        onClick={() => toggleFaq(faq.id)}
                                        aria-expanded={isOpen}
                                    >
                                        <span className="home-faq-question">
                                            <FiHelpCircle className="home-faq-icon" aria-hidden="true" />
                                            {faq.question}
                                        </span>
                                        <span className="home-faq-toggle-btn" aria-hidden="true">
                                            {isOpen ? <FiMinus /> : <FiPlus />}
                                        </span>
                                    </button>
                                    {isOpen && (
                                        <div className="home-faq-content">
                                            <p>{faq.answer}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Right: Showroom Guidance Visual Card with Tap to Enlarge */}
                    <div className="home-faq-showcase">
                        <div
                            className="home-faq-showcase__frame"
                            onClick={() =>
                                openLightbox(
                                    FAQ_SHOWROOM_IMG,
                                    'Consultation & Smart Appliance Guidance at Rahber Corporation',
                                    'Lahore Showroom Consultation Desk: Sanda Kalan'
                                )
                            }
                            title="Click or tap to view showroom photo"
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    openLightbox(
                                        FAQ_SHOWROOM_IMG,
                                        'Consultation & Smart Appliance Guidance at Rahber Corporation',
                                        'Lahore Showroom Consultation Desk: Sanda Kalan'
                                    );
                                }
                            }}
                        >
                            <img
                                src={FAQ_SHOWROOM_IMG}
                                alt="Smart Appliance Guidance at Rahber Corporation Showroom"
                                className="home-faq-showcase__img"
                                loading="lazy"
                            />
                        </div>
                        <div className="home-faq-showcase__info">
                            <h4>Still have questions?</h4>
                            <p>Speak directly with our Lahore showroom sales advisors for custom payment plans.</p>
                            <div className="home-faq-showcase__actions">
                                <a
                                    href={BUSINESS_WHATSAPP_CHAT_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="home-faq-showcase__btn home-faq-showcase__btn--wa"
                                >
                                    <FaWhatsapp /> Ask on WhatsApp
                                </a>
                                <Link to="/contact" className="home-faq-showcase__btn home-faq-showcase__btn--phone">
                                    <FiPhoneCall /> Contact Showroom
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="home-faq-footer">
                    <p>Looking for detailed warranty and exchange terms?</p>
                    <Link to="/faq" className="btn btn-outline home-faq-more-btn">
                        View All FAQs &amp; Store Policies →
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default HomeFaqSection;
